import { forwardRef, useImperativeHandle } from "react";
import { FaRotateLeft } from "react-icons/fa6";
import { Alert } from "reactstrap";
import useBeforeLeaving from "../../hook/useBeforeLeaving";
import { notifySuccess, notifyWarning } from "../../services/notification";
import confirm from "./confirm";

/**
 * Unsaved changes warning
 */

export interface ChangeAlertRef {
  complete: (successful: boolean) => void; // notify that the changes are saved
}

interface ChangeAlertProps {
  isOpen: boolean;
  isChanged?: boolean; // allow to hide the alert bar but still detect leaving
  itemName?: string;
  onDiscard: () => void;
}

const ChangeAlert = forwardRef<ChangeAlertRef, ChangeAlertProps>((props, ref) => {
  const { isOpen, isChanged = isOpen, itemName, onDiscard } = props;

  const { continueToLeave } = useBeforeLeaving(() => !isChanged, () => {
    let msg = 'If you leave, any changes you made will not be saved. Do you want to continue?';
    if (itemName) {
      msg = `You are currently editing ${itemName}. ${msg}`;
    }

    confirm({
      title: 'Unsaved changes',
      content: msg,
      okText: <><FaRotateLeft /> Discard changes and continue</>,
      okBtnColor: 'danger',
      titleCenter: true,
      bodyCenter: true
    }).then(() => {
      onDiscard();
      setTimeout(continueToLeave, 100);
    });
  });

  useImperativeHandle(ref, () => ({
    complete(successful: boolean): void {
      successful ? notifySuccess() : notifyWarning('The changes is not saved.', 5000);
    }
  }));

  return (
    <Alert color="info" className="m-0" isOpen={isOpen}>
      Please save your changes.
    </Alert>
  );
});

export default ChangeAlert;
